import { useParams, useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import { useInspectionForm } from "./useInspectionForm";
import { useArborInspectionDetail, useTreeDetail } from "@/hooks";
import { inspectionApi, type InspectionReadinessApi } from "@/lib/api";
import type { ArborInspectionDetail } from "@/types/arboricultural-inspection";
import type { TreeApi } from "@/lib/api";

import StepIdentificacao from "./components/StepIdentificacao";
import StepDendrometria from "./components/StepDendrometria";
import StepFitossanidade from "./components/StepFitossanidade";
import StepBiomecanica from "./components/StepBiomecanica";
import StepConflitos from "./components/StepConflitos";
import StepRisco from "./components/StepRisco";
import StepParecer from "./components/StepParecer";
import StepEvidencias from "./components/StepEvidencias";
import StepResponsabilidade from "./components/StepResponsabilidade";
import StepRevisao from "./components/StepRevisao";
import ReviewPanel from "./components/ReviewPanel";

const STEPS = [
  { label: "Identificação", icon: "ri-tree-line" },
  { label: "Dendrometria", icon: "ri-ruler-line" },
  { label: "Fitossanidade", icon: "ri-heart-pulse-line" },
  { label: "Biomecânica", icon: "ri-anchor-line" },
  { label: "Conflitos", icon: "ri-flashlight-line" },
  { label: "Risco", icon: "ri-alert-line" },
  { label: "Parecer", icon: "ri-stethoscope-line" },
  { label: "Evidências", icon: "ri-camera-line" },
  { label: "Responsável", icon: "ri-user-star-line" },
  { label: "Revisão", icon: "ri-checkbox-circle-line" },
];

export default function ArborInspectionPage() {
  const { treeId, inspectionId: inspIdParam } = useParams<{ treeId: string; inspectionId?: string }>();
  const navigate = useNavigate();
  const isNew = !inspIdParam;

  const [createdId, setCreatedId] = useState<number | null>(null);
  const effectiveId = inspIdParam ? parseInt(inspIdParam) : createdId;

  const { state, dispatch, save } = useInspectionForm(effectiveId);
  const { inspection: detail, refetch } = useArborInspectionDetail(effectiveId);
  const numericTreeId = treeId ? parseInt(treeId) : null;
  const { data: treeRaw } = useTreeDetail(numericTreeId);
  const [submitting, setSubmitting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [readiness, setReadiness] = useState<InspectionReadinessApi | null>(null);

  // O servidor e quem diz o que o manual ainda exige; a tela so mostra.
  const refreshReadiness = useCallback(async () => {
    if (!effectiveId) return;
    try {
      setReadiness(await inspectionApi.readiness(effectiveId));
    } catch {
      setReadiness(null);
    }
  }, [effectiveId]);

  useEffect(() => {
    if (detail) {
      dispatch({ type: "LOAD_FROM_API", detail });
    }
  }, [detail, dispatch]);

  const treeData = treeRaw
    ? {
        ctaCode: (treeRaw as TreeApi).cta_code ?? "",
        species: (treeRaw as TreeApi).species_popular_name ?? "",
        scientificName: (treeRaw as TreeApi).species_scientific_name ?? "",
        address: [
          (treeRaw as TreeApi).street,
          (treeRaw as TreeApi).neighborhood,
        ].filter(Boolean).join(", "),
      }
    : null;

  const evidence: ArborInspectionDetail["evidence"] = detail?.evidence ?? [];

  const handleCreate = useCallback(async () => {
    if (!treeId) return;
    try {
      const resp = await inspectionApi.create(parseInt(treeId), {
        inspection_type: "ARBORICULTURAL_TECHNICAL",
        inspection_date: new Date().toISOString().slice(0, 10),
      });
      const newId = (resp as { id: number }).id;
      setCreatedId(newId);
      navigate(`/cta/${treeId}/inspecoes/${newId}`, { replace: true });
    } catch {
      setActionError("Erro ao criar inspeção.");
    }
  }, [treeId, navigate]);

  useEffect(() => {
    if (isNew && !createdId) {
      handleCreate();
    }
  }, [isNew, createdId, handleCreate]);

  const handleSaveDraft = useCallback(async () => {
    await save();
  }, [save]);

  const handleSubmit = useCallback(async () => {
    if (!effectiveId) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await save();
      await inspectionApi.submit(effectiveId);
      refetch();
    } catch (err) {
      setActionError(err instanceof Error && err.message ? err.message : "Erro ao submeter inspeção.");
      void refreshReadiness();
    } finally {
      setSubmitting(false);
    }
  }, [effectiveId, save, refetch, refreshReadiness]);

  const handleEvidenceChange = useCallback(() => {
    refetch();
  }, [refetch]);

  const currentStep = state.currentStep;
  const status = detail?.workflowStatus;
  // Quem coleta edita; depois do envio o registro e do revisor (a devolucao reabre a edicao).
  const isEditable = !status || status === "DRAFT" || status === "IN_PROGRESS" || status === "RETURNED";
  const isImmutable = !isEditable;

  // Ao chegar na revisao grava o que esta na tela e le as pendencias do servidor.
  useEffect(() => {
    if (!effectiveId) return;
    if (currentStep !== STEPS.length - 1 && status !== "SUBMITTED" && status !== "UNDER_REVIEW") return;
    let cancelled = false;
    (async () => {
      if (isEditable) await save();
      if (!cancelled) await refreshReadiness();
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, effectiveId, status]);

  const goNext = () => {
    if (currentStep < STEPS.length - 1) dispatch({ type: "SET_STEP", step: currentStep + 1 });
  };
  const goPrev = () => {
    if (currentStep > 0) dispatch({ type: "SET_STEP", step: currentStep - 1 });
  };

  const saveStatusLabel = (() => {
    switch (state.saveStatus) {
      case "saving": return "Salvando...";
      case "saved": return state.lastSavedAt ? `Salvo às ${state.lastSavedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}` : "Salvo";
      case "error": return "Erro ao salvar";
      default: return state.isDirty ? "Alterações não salvas" : "";
    }
  })();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-background-200/70 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-background-100 transition-colors"
          >
            <i className="ri-arrow-left-line text-foreground-500"></i>
          </button>
          <div>
            <h1 className="text-base font-semibold text-foreground-800">
              {isNew ? "Nova Inspeção Arbórea" : `Inspeção #${effectiveId}`}
            </h1>
            <p className="text-xs text-foreground-400">
              {treeData?.ctaCode && `Árvore ${treeData.ctaCode}`}
              {detail?.workflowStatus && (
                <span className="ml-2 px-2 py-0.5 bg-background-100 text-foreground-500 rounded-full text-[10px] font-bold">
                  {detail.workflowStatus}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`text-xs ${state.saveStatus === "error" ? "text-danger-500" : "text-foreground-400"}`}
            title={state.saveError ?? undefined}
          >
            {saveStatusLabel}
          </span>
          {isImmutable && (
            <span className="px-3 py-1 bg-warning-50 text-warning-700 rounded-lg text-xs font-medium">
              <i className="ri-lock-line mr-1"></i> Somente leitura
            </span>
          )}
        </div>
      </div>

      {detail && (
        <ReviewPanel
          detail={detail}
          readiness={readiness}
          onChanged={() => {
            refetch();
            void refreshReadiness();
          }}
        />
      )}

      {state.saveStatus === "error" && state.saveError && (
        <div className="mx-6 mt-3 rounded-lg border border-danger-200 bg-danger-50 px-4 py-2.5 text-sm text-danger-700 flex gap-2" role="alert">
          <i className="ri-error-warning-line mt-0.5"></i>
          <span>
            <strong>Não foi possível salvar.</strong> {state.saveError} Corrija o campo; nada foi perdido nesta tela.
          </span>
        </div>
      )}

      {/* Step indicator */}
      <div className="bg-white border-b border-background-200/70 px-6 py-3 overflow-x-auto">
        <div className="flex items-center justify-center min-w-max">
          {STEPS.map((step, idx) => {
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            return (
              <div key={idx} className="flex items-center">
                <button
                  type="button"
                  onClick={() => dispatch({ type: "SET_STEP", step: idx })}
                  className="flex flex-col items-center gap-1 group"
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                      isActive
                        ? "bg-primary-100 text-primary-700 border-2 border-primary-300"
                        : isCompleted
                        ? "bg-primary-500 text-white"
                        : "bg-background-100 text-foreground-400 group-hover:bg-background-200"
                    }`}
                  >
                    {isCompleted ? <i className="ri-check-line text-sm"></i> : idx + 1}
                  </div>
                  <span
                    className={`text-[10px] font-medium whitespace-nowrap ${
                      isActive ? "text-primary-700" : "text-foreground-400"
                    }`}
                  >
                    {step.label}
                  </span>
                </button>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`w-8 h-0.5 mx-1 mt-[-12px] ${
                      idx < currentStep ? "bg-primary-500" : "bg-background-200"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto p-6">
        <fieldset disabled={isImmutable && currentStep !== 7} className="max-w-4xl mx-auto border-0 p-0 m-0 min-w-0">
          {currentStep === 0 && (
            <StepIdentificacao state={state} dispatch={dispatch} treeData={treeData} />
          )}
          {currentStep === 1 && <StepDendrometria state={state} dispatch={dispatch} />}
          {currentStep === 2 && <StepFitossanidade state={state} dispatch={dispatch} />}
          {currentStep === 3 && <StepBiomecanica state={state} dispatch={dispatch} />}
          {currentStep === 4 && <StepConflitos state={state} dispatch={dispatch} />}
          {currentStep === 5 && <StepRisco state={state} dispatch={dispatch} />}
          {currentStep === 6 && <StepParecer state={state} dispatch={dispatch} />}
          {currentStep === 7 && (
            <StepEvidencias
              inspectionId={effectiveId ?? null}
              evidence={evidence}
              onEvidenceChange={handleEvidenceChange}
              workflowStatus={status}
            />
          )}
          {currentStep === 8 && <StepResponsabilidade state={state} dispatch={dispatch} />}
          {currentStep === 9 && (
            <StepRevisao state={state} evidence={evidence} treeData={treeData} readiness={readiness} />
          )}
        </fieldset>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-background-200/70 px-3 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={goPrev}
          disabled={currentStep === 0}
          className="px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-800 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <i className="ri-arrow-left-s-line sm:mr-1"></i><span className="hidden sm:inline"> Anterior</span>
        </button>

        <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-3">
          {actionError && <span className="w-full sm:w-auto text-xs text-danger-500 text-right">{actionError}</span>}

          {!isImmutable && (
            <button
              type="button"
              onClick={handleSaveDraft}
              disabled={!state.isDirty || state.saveStatus === "saving"}
              className="px-4 py-2 text-sm font-medium border border-background-200 rounded-lg hover:bg-background-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-save-line sm:mr-1"></i><span className="hidden sm:inline"> Salvar rascunho</span>
            </button>
          )}

          {currentStep === STEPS.length - 1 ? (
            !isImmutable && (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || (readiness != null && !readiness.can_submit)}
                title={readiness != null && !readiness.can_submit ? "Resolva as pendências obrigatórias para enviar" : undefined}
                className="px-5 py-2 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1"></i> Submetendo...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-1"></i> {status === "RETURNED" ? "Reenviar inspeção" : "Submeter inspeção"}
                  </>
                )}
              </button>
            )
          ) : (
            <button
              type="button"
              onClick={goNext}
              className="px-5 py-2 text-sm font-semibold bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Continuar <i className="ri-arrow-right-s-line ml-1"></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
